import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Phone, Calendar, AlertCircle, UserPlus, X as XIcon, Edit2 } from "lucide-react";
import { createAppointment, createReferrer, formatApiError } from "@/lib/api";
import { CABINET, DOMAINS, DOMAIN_LABEL, SLOT_LABEL, LOCATION_LABEL, LOCATION_OPTIONS } from "@/lib/content";

// Helper: generate next 30 available weekdays
function generateDates() {
    const dates = [];
    const today = new Date();
    let d = new Date(today);
    d.setDate(d.getDate() + 1); // start tomorrow
    while (dates.length < 30) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) {
            dates.push(new Date(d));
        }
        d.setDate(d.getDate() + 1);
    }
    return dates;
}

const SLOTS = [
    { value: "matin", label: "Matin (08h30 – 12h00)" },
    { value: "apres-midi", label: "Après-midi (13h30 – 18h00)" },
];

export default function Appointment() {
    const [step, setStep] = useState(1);       // 1: form, 2: submitting, 3: success
    const [savedReferrer, setSavedReferrer] = useState(null);
    const [referrerModalOpen, setReferrerModalOpen] = useState(false);
    const [form, setForm] = useState({
        patient_name: "",
        patient_email: "",
        patient_phone: "",
        domain: "",
        location: "cabinet",
        referring_doctor: "",
        preferred_date: "",
        preferred_slot: "",
        message: "",
    });
    const [error, setError] = useState(null);
    const [confirmation, setConfirmation] = useState(null);

    const dates = generateDates();

    // Load saved referrer from localStorage and pre-fill referring_doctor
    useEffect(() => {
        try {
            const raw = localStorage.getItem("ana_referrer");
            if (raw) {
                const r = JSON.parse(raw);
                setSavedReferrer(r);
                setForm((f) => ({
                    ...f,
                    referring_doctor: r.cabinet ? `${r.name} — ${r.cabinet}` : r.name,
                }));
            }
        } catch {}
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const saveReferrer = async (data) => {
        // Send to backend directory (fire-and-forget) — non-blocking
        try {
            await createReferrer(data);
        } catch {}
        localStorage.setItem("ana_referrer", JSON.stringify(data));
        setSavedReferrer(data);
        setForm((f) => ({
            ...f,
            referring_doctor: data.cabinet ? `${data.name} — ${data.cabinet}` : data.name,
        }));
        setReferrerModalOpen(false);
    };

    const clearReferrer = () => {
        localStorage.removeItem("ana_referrer");
        setSavedReferrer(null);
        setForm((f) => ({ ...f, referring_doctor: "" }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setStep(2);
        try {
            const created = await createAppointment(form);
            setConfirmation(created);
            setStep(3);
        } catch (e) {
            setError(formatApiError(e));
            setStep(1);
        }
    };

    const canSubmit =
        form.patient_name.length >= 2 &&
        form.patient_email.includes("@") &&
        form.patient_phone.length >= 6 &&
        form.domain &&
        form.location &&
        form.preferred_date &&
        form.preferred_slot;

    return (
        <main data-testid="appointment-page" style={{ paddingTop: 140, minHeight: "100vh" }}>
            <ReferrerModal
                open={referrerModalOpen}
                initial={savedReferrer}
                onClose={() => setReferrerModalOpen(false)}
                onSave={saveReferrer}
            />
            <section style={{ padding: "40px 40px 40px" }}>
                <div className="mx-auto grid grid-cols-1 md:grid-cols-12 gap-16" style={{ maxWidth: 1400 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="md:col-span-5"
                    >
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Prise de rendez-vous</div>
                        <div className="divider-terra mt-3" />
                        <h1
                            className="font-display mt-8"
                            style={{
                                fontSize: "clamp(2.4rem, 5.5vw, 4.5rem)",
                                lineHeight: 0.95,
                                color: "var(--forest-dark)",
                                fontWeight: 400,
                                letterSpacing: "-0.015em",
                            }}
                        >
                            Un temps
                            <br />
                            <span style={{ fontStyle: "italic", color: "var(--terra)" }}>pour respirer</span>,
                            <br />un temps pour <span style={{ fontStyle: "italic" }}>soigner.</span>
                        </h1>

                        <p className="font-display mt-8" style={{ fontSize: "1.05rem", lineHeight: 1.6, color: "var(--ink-mid)", fontStyle: "italic", maxWidth: 400 }}>
                            Remplissez le formulaire et Ana vous recontacte sous 48h ouvrables pour confirmer un créneau. La demande n'est pas une confirmation automatique.
                        </p>

                        <div className="mt-12" style={{ padding: "24px 24px", background: "var(--cream-warm)", border: "1px solid var(--hairline)", borderRadius: 4 }}>
                            <div className="font-mono-clinical" style={{ color: "var(--forest)" }}>Urgence ou question rapide</div>
                            <div className="mt-3 flex items-center gap-3">
                                <Phone size={16} style={{ color: "var(--terra)" }} />
                                <a
                                    href={`tel:${CABINET.phoneIntl}`}
                                    className="link-underline"
                                    data-testid="appointment-phone"
                                    style={{ fontSize: 18, color: "var(--forest-dark)", fontWeight: 500 }}
                                >
                                    {CABINET.phone}
                                </a>
                            </div>
                            <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 8 }}>
                                Lundi au vendredi · 08h30–12h00 · 13h30–18h00
                            </div>
                        </div>
                    </motion.div>

                    {/* Form column */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.15 }}
                        className="md:col-span-7"
                    >
                        {step === 3 ? (
                            <SuccessPanel confirmation={confirmation} onNew={() => { setForm({ patient_name: "", patient_email: "", patient_phone: "", domain: "", location: "cabinet", referring_doctor: "", preferred_date: "", preferred_slot: "", message: "" }); setConfirmation(null); setStep(1); }} />
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="appointment-form"
                                style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: "36px 36px", borderRadius: 4 }}
                            >
                                {/* Honeypot — hidden from users, only bots fill this */}
                                <input
                                    type="text"
                                    name="website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                    value={form.website || ""}
                                    onChange={(e) => set("website", e.target.value)}
                                    style={{
                                        position: "absolute", left: "-9999px",
                                        width: 1, height: 1, opacity: 0,
                                        pointerEvents: "none",
                                    }}
                                />
                                {/* Progress */}
                                <div className="flex items-center gap-4 mb-10">
                                    <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Étape 1 / 1 · Demande</div>
                                    <div style={{ flex: 1, height: 1, background: "var(--hairline)" }} />
                                    <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>Réponse sous 48h</div>
                                </div>

                                {/* Referrer directory chip */}
                                <div style={{ marginBottom: 28 }}>
                                    {savedReferrer ? (
                                        <div
                                            data-testid="referrer-chip"
                                            style={{
                                                display: "flex", alignItems: "center", gap: 12,
                                                padding: "10px 16px",
                                                background: "var(--cream-warm)",
                                                border: "1px solid var(--terra-light)",
                                                borderRadius: 999,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--terra)" }}>
                                                Adressé par
                                            </span>
                                            <span className="font-display" style={{ fontSize: 15, color: "var(--forest-dark)", fontStyle: "italic" }}>
                                                {savedReferrer.name}{savedReferrer.cabinet ? ` — ${savedReferrer.cabinet}` : ""}
                                            </span>
                                            <div style={{ flex: 1 }} />
                                            <button
                                                type="button"
                                                data-testid="referrer-edit"
                                                onClick={() => setReferrerModalOpen(true)}
                                                style={{ background: "transparent", border: 0, cursor: "pointer", color: "var(--forest)", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}
                                            >
                                                <Edit2 size={13} /> Modifier
                                            </button>
                                            <button
                                                type="button"
                                                data-testid="referrer-clear"
                                                onClick={clearReferrer}
                                                aria-label="Retirer"
                                                style={{ background: "transparent", border: 0, cursor: "pointer", color: "var(--ink-soft)", padding: 2 }}
                                            >
                                                <XIcon size={14} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid="referrer-open"
                                            onClick={() => setReferrerModalOpen(true)}
                                            className="btn-ghost"
                                            style={{ fontSize: 12, padding: "8px 14px" }}
                                        >
                                            <UserPlus size={14} /> Vous êtes médecin&nbsp;? Enregistrer votre cabinet
                                        </button>
                                    )}
                                </div>

                                {/* Patient */}
                                <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                                    <legend className="font-mono-clinical mb-4" style={{ color: "var(--forest)" }}>Patient·e</legend>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                                        <div>
                                            <label className="field-label">Nom complet</label>
                                            <input
                                                data-testid="appt-name"
                                                required minLength={2}
                                                className="field-clinical"
                                                value={form.patient_name}
                                                onChange={(e) => set("patient_name", e.target.value)}
                                                placeholder="Prénom Nom"
                                            />
                                        </div>
                                        <div>
                                            <label className="field-label">Téléphone</label>
                                            <input
                                                data-testid="appt-phone"
                                                required minLength={6}
                                                className="field-clinical"
                                                value={form.patient_phone}
                                                onChange={(e) => set("patient_phone", e.target.value)}
                                                placeholder="079 XXX XX XX"
                                                inputMode="tel"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="field-label">Adresse email</label>
                                            <input
                                                data-testid="appt-email"
                                                type="email" required
                                                className="field-clinical"
                                                value={form.patient_email}
                                                onChange={(e) => set("patient_email", e.target.value)}
                                                placeholder="patient@exemple.ch"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="field-label">Médecin référent (optionnel)</label>
                                            <input
                                                data-testid="appt-doctor"
                                                className="field-clinical"
                                                value={form.referring_doctor}
                                                onChange={(e) => set("referring_doctor", e.target.value)}
                                                placeholder="Dr / Dresse — cabinet"
                                            />
                                        </div>
                                    </div>
                                </fieldset>

                                {/* Domain */}
                                <fieldset style={{ border: 0, padding: 0, margin: "24px 0 0" }}>
                                    <legend className="font-mono-clinical mb-4" style={{ color: "var(--forest)" }}>Domaine de prise en charge</legend>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {[...DOMAINS, { id: "autre", num: "04", title: "Autre / à préciser", subtitle: "Question générale" }].map((d) => (
                                            <label
                                                key={d.id}
                                                data-testid={`appt-domain-${d.id}`}
                                                style={{
                                                    display: "block",
                                                    padding: "14px 16px",
                                                    border: `1px solid ${form.domain === d.id ? "var(--forest-dark)" : "var(--hairline)"}`,
                                                    background: form.domain === d.id ? "var(--forest-dark)" : "transparent",
                                                    color: form.domain === d.id ? "var(--cream)" : "var(--forest-dark)",
                                                    borderRadius: 3,
                                                    cursor: "pointer",
                                                    transition: "all 0.25s ease",
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="domain"
                                                    value={d.id}
                                                    checked={form.domain === d.id}
                                                    onChange={(e) => set("domain", e.target.value)}
                                                    style={{ display: "none" }}
                                                    required
                                                />
                                                <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.14em" }}>
                                                    {d.num} · {d.subtitle || ""}
                                                </div>
                                                <div className="font-display mt-1" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.2 }}>
                                                    {d.title}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>

                                {/* Location */}
                                <fieldset style={{ border: 0, padding: 0, margin: "32px 0 0" }}>
                                    <legend className="font-mono-clinical mb-4" style={{ color: "var(--forest)" }}>Lieu du traitement</legend>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {LOCATION_OPTIONS.map((opt) => (
                                            <label
                                                key={opt.id}
                                                data-testid={`appt-location-${opt.id}`}
                                                style={{
                                                    display: "block",
                                                    padding: "16px 18px",
                                                    border: `1px solid ${form.location === opt.id ? "var(--terra)" : "var(--hairline)"}`,
                                                    background: form.location === opt.id ? "var(--cream-warm)" : "transparent",
                                                    borderRadius: 3,
                                                    cursor: "pointer",
                                                    transition: "all 0.25s ease",
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="location"
                                                    value={opt.id}
                                                    checked={form.location === opt.id}
                                                    onChange={(e) => set("location", e.target.value)}
                                                    style={{ display: "none" }}
                                                />
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="font-display" style={{ fontSize: 17, fontWeight: 500, color: "var(--forest-dark)", lineHeight: 1.2 }}>
                                                        {opt.title}
                                                    </div>
                                                    {form.location === opt.id && (
                                                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--terra)" }} />
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 11, color: "var(--terra)", textTransform: "uppercase", letterSpacing: "0.12em", marginTop: 4 }}>
                                                    {opt.subtitle}
                                                </div>
                                                <div style={{ fontSize: 12, color: "var(--ink-mid)", marginTop: 6, lineHeight: 1.4 }}>
                                                    {opt.body}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>

                                {/* Slot */}
                                <fieldset style={{ border: 0, padding: 0, margin: "32px 0 0" }}>
                                    <legend className="font-mono-clinical mb-4" style={{ color: "var(--forest)" }}>Créneau souhaité</legend>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div>
                                            <label className="field-label">Date préférée</label>
                                            <select
                                                data-testid="appt-date"
                                                required
                                                className="field-clinical"
                                                value={form.preferred_date}
                                                onChange={(e) => set("preferred_date", e.target.value)}
                                            >
                                                <option value="">Choisir une date…</option>
                                                {dates.map((d) => {
                                                    const iso = d.toISOString().slice(0, 10);
                                                    return (
                                                        <option key={iso} value={iso}>
                                                            {d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long" })}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="field-label">Moment de la journée</label>
                                            <select
                                                data-testid="appt-slot"
                                                required
                                                className="field-clinical"
                                                value={form.preferred_slot}
                                                onChange={(e) => set("preferred_slot", e.target.value)}
                                            >
                                                <option value="">Choisir un moment…</option>
                                                {SLOTS.map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </fieldset>

                                {/* Message */}
                                <div style={{ margin: "32px 0 0" }}>
                                    <label className="field-label">Motif ou informations complémentaires (optionnel)</label>
                                    <textarea
                                        data-testid="appt-message"
                                        className="field-clinical"
                                        rows={3}
                                        value={form.message}
                                        onChange={(e) => set("message", e.target.value)}
                                        placeholder="Motif, ordonnance en cours, informations pertinentes…"
                                        style={{ resize: "vertical", fontFamily: "var(--font-body)" }}
                                    />
                                </div>

                                {error && (
                                    <div className="mt-6 flex items-start gap-2" style={{ background: "rgba(198, 68, 68, 0.08)", padding: "12px 16px", borderRadius: 4, fontSize: 13, color: "#a63333" }}>
                                        <AlertCircle size={14} style={{ marginTop: 2 }} />
                                        <span>{String(error)}</span>
                                    </div>
                                )}

                                <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
                                    <div style={{ fontSize: 12, color: "var(--ink-soft)", maxWidth: 400 }}>
                                        En envoyant cette demande, vous acceptez d'être recontacté·e par le cabinet
                                        pour confirmer le rendez-vous.
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="appt-submit"
                                        disabled={!canSubmit || step === 2}
                                        className="btn-primary"
                                        style={{
                                            opacity: !canSubmit || step === 2 ? 0.5 : 1,
                                            cursor: !canSubmit || step === 2 ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        {step === 2 ? "Envoi en cours…" : "Envoyer la demande"}
                                        <ArrowRight size={16} />
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </div>
            </section>
        </main>
    );
}

function SuccessPanel({ confirmation, onNew }) {    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-testid="appointment-success"
            style={{ background: "var(--forest-deep)", color: "var(--cream)", padding: "48px 40px", borderRadius: 4, position: "relative", overflow: "hidden" }}
        >
            <div aria-hidden className="breathe-slow" style={{ position: "absolute", right: -80, top: -80, width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.2)" }} />
            <div style={{ position: "relative", zIndex: 2 }}>
                <div
                    className="breathe"
                    style={{
                        width: 60, height: 60,
                        borderRadius: "50%",
                        background: "rgba(216, 155, 146, 0.2)",
                        border: "1px solid var(--terra)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "var(--terra-light)",
                    }}
                >
                    <Check size={26} />
                </div>
                <div className="font-mono-clinical mt-8" style={{ color: "var(--terra-light)" }}>
                    Demande enregistrée
                </div>
                <div className="mt-2" style={{ height: 1, width: 40, background: "var(--terra)" }} />
                <h2 className="font-display mt-6" style={{ fontSize: "2.6rem", lineHeight: 1.1, color: "var(--cream)", fontWeight: 400 }}>
                    Merci, votre demande <span style={{ fontStyle: "italic", color: "var(--terra-light)" }}>a bien été reçue.</span>
                </h2>
                <p className="font-display mt-6" style={{ fontSize: "1.05rem", lineHeight: 1.65, color: "rgba(251, 248, 243, 0.85)", maxWidth: 520 }}>
                    Ana vous recontactera sous 48 heures ouvrables au numéro que vous avez indiqué. En cas d'urgence, appelez directement le cabinet.
                </p>

                {confirmation && (
                    <div className="mt-8" style={{ background: "rgba(255,255,255,0.06)", padding: "20px 24px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.1)" }}>
                        <div className="font-mono-clinical" style={{ color: "var(--terra-light)" }}>Récapitulatif</div>
                        <div className="mt-4 grid grid-cols-2 gap-4" style={{ fontSize: 14 }}>
                            <div>
                                <div style={{ color: "var(--terra-light)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em" }}>Nom</div>
                                <div className="mt-1">{confirmation.patient_name}</div>
                            </div>
                            <div>
                                <div style={{ color: "var(--terra-light)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em" }}>Domaine</div>
                                <div className="mt-1">{DOMAIN_LABEL[confirmation.domain] || confirmation.domain}</div>
                            </div>
                            <div>
                                <div style={{ color: "var(--terra-light)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em" }}>Date souhaitée</div>
                                <div className="mt-1 flex items-center gap-2"><Calendar size={13} /> {new Date(confirmation.preferred_date).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" })}</div>
                            </div>
                            <div>
                                <div style={{ color: "var(--terra-light)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em" }}>Moment</div>
                                <div className="mt-1">{SLOT_LABEL[confirmation.preferred_slot] || confirmation.preferred_slot}</div>
                            </div>
                            <div>
                                <div style={{ color: "var(--terra-light)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em" }}>Lieu</div>
                                <div className="mt-1">{LOCATION_LABEL[confirmation.location] || "En cabinet"}</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-10 flex gap-4 flex-wrap">
                    <button onClick={onNew} className="btn-ghost" style={{ color: "var(--cream)", borderColor: "rgba(255,255,255,0.3)" }} data-testid="appt-new">
                        Faire une autre demande
                    </button>
                </div>
            </div>
        </motion.div>
    );
}

function ReferrerModal({ open, initial, onClose, onSave }) {
    const [name, setName] = useState(initial?.name || "");
    const [cabinet, setCabinet] = useState(initial?.cabinet || "");
    const [email, setEmail] = useState(initial?.email || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(initial?.name || "");
            setCabinet(initial?.cabinet || "");
            setEmail(initial?.email || "");
        }
    }, [open, initial]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        await onSave({
            name: name.trim(),
            cabinet: cabinet.trim() || null,
            email: email.trim() || null,
        });
        setSaving(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="referrer-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                        position: "fixed", inset: 0,
                        background: "rgba(107, 85, 72, 0.55)",
                        backdropFilter: "blur(6px)",
                        WebkitBackdropFilter: "blur(6px)",
                        zIndex: 100,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 24,
                    }}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.96 }}
                        transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "var(--cream)",
                            border: "1px solid var(--hairline)",
                            padding: "40px 40px 32px",
                            borderRadius: 4,
                            maxWidth: 520, width: "100%",
                            position: "relative",
                        }}
                    >
                        <button
                            data-testid="referrer-modal-close"
                            onClick={onClose}
                            aria-label="Fermer"
                            style={{ position: "absolute", top: 16, right: 16, background: "transparent", border: 0, cursor: "pointer", color: "var(--ink-soft)" }}
                        >
                            <XIcon size={20} />
                        </button>

                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Médecin référent</div>
                        <div className="divider-terra mt-3" />
                        <h3 className="font-display mt-6" style={{ fontSize: "1.9rem", lineHeight: 1.1, color: "var(--forest-dark)", fontWeight: 400 }}>
                            Enregistrez votre <span style={{ fontStyle: "italic", color: "var(--terra)" }}>cabinet</span>.
                        </h3>
                        <p className="font-display mt-3" style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-mid)", fontStyle: "italic" }}>
                            Vos informations seront pré-remplies à chaque nouvelle demande adressée par vos soins. Consultables uniquement par Ana.
                        </p>

                        <form onSubmit={submit} data-testid="referrer-form" className="mt-8">
                            <div className="mb-6">
                                <label className="field-label">Nom du médecin</label>
                                <input
                                    data-testid="referrer-name"
                                    required minLength={2}
                                    className="field-clinical"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Dr / Dresse Prénom Nom"
                                />
                            </div>
                            <div className="mb-6">
                                <label className="field-label">Cabinet / structure</label>
                                <input
                                    data-testid="referrer-cabinet"
                                    className="field-clinical"
                                    value={cabinet}
                                    onChange={(e) => setCabinet(e.target.value)}
                                    placeholder="Cabinet du Léman, CHUV, EMS…"
                                />
                            </div>
                            <div>
                                <label className="field-label">Email (optionnel)</label>
                                <input
                                    data-testid="referrer-email"
                                    type="email"
                                    className="field-clinical"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="pour recevoir les comptes-rendus"
                                />
                            </div>

                            <div className="mt-10 flex items-center justify-between gap-4">
                                <button type="button" onClick={onClose} className="link-underline" style={{ fontSize: 13, color: "var(--ink-soft)", background: "transparent", border: 0, cursor: "pointer" }}>
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    data-testid="referrer-save"
                                    disabled={!name || saving}
                                    className="btn-primary"
                                    style={{ opacity: !name || saving ? 0.5 : 1 }}
                                >
                                    {saving ? "Enregistrement…" : "Enregistrer"} <ArrowRight size={14} />
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

